import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FiEye, FiEdit, FiDollarSign, FiLock } from "react-icons/fi";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy, doc, updateDoc, arrayUnion } from "firebase/firestore";
import toast from "react-hot-toast";
import { setLoading } from "../redux/loaderSlice";
import { useDispatch } from "react-redux";

/* ================= STYLES ================= */

const Wrapper = styled.div`
  padding: 20px;
  margin-top: 60px; /* To offset fixed header */
`;

/* ===== SUMMARY CARDS ===== */
const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.div`
  background: #fff;
  padding: 14px;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.05);
`;

const Title = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Value = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-top: 5px;
`;

/* ===== TABLE ===== */
const TableContainer = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 10px;
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 6px;
  font-size: 14px;
  background: #f9fafb;
`;

const Td = styled.td`
  padding: 8px 6px;
  font-size: 14px;
  border-bottom: 1px solid #eee;
`;

/* ===== STATUS ===== */
const Status = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  ${({ type }) =>
    type === "active" && `background:#DCFCE7;color:#166534;`}

  ${({ type }) =>
    type === "closed" && `background:#E5E7EB;color:#374151;`}

  ${({ type }) =>
    type === "overdue" && `background:#FEE2E2;color:#991B1B;`}
`;

/* ===== ACTIONS ===== */
const Actions = styled.div`
  display: flex;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 10px;
    flex-wrap: wrap;
  }
`;

const Btn = styled.button`
  min-width: 36px;
  min-height: 36px;
  font-size: 18px;

  border-radius: 8px;
  border: none;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    min-width: 42px;
    min-height: 42px;
    font-size: 20px;
  }

  ${({ type }) =>
    type === "view" && `background:#DBEAFE;color:#1D4ED8;`}

  ${({ type }) =>
    type === "edit" && `background:#FEF3C7;color:#92400E;`}

  ${({ type }) =>
    type === "pay" && `background:#D1FAE5;color:#065F46;`}

  ${({ type }) =>
    type === "close" && `background:#FEE2E2;color:#991B1B;`}
`;

/* ===== MOBILE CARDS ===== */
const MobileContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const MobileCard = styled.div`
  background: #fff;
  padding: 14px;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.05);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 13px;
`;

const Label = styled.span`
  color: #6b7280;
`;

const Val = styled.span`
  font-weight: 600;
`;

/* ===== MODAL STYLES ===== */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const FormInput = styled.input`
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #f59e0b;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
`;

const ModalBtn = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  background: ${({ primary }) => (primary ? "#f59e0b" : "#e5e7eb")};
  color: ${({ primary }) => (primary ? "#fff" : "#374151")};
  &:hover {
    opacity: 0.9;
  }
`;

/* ================= COMPONENT ================= */

export default function GoldLoan() {
  const dispatch = useDispatch();
  const [loans, setLoans] = useState([]);

  // Actions State
  const [actionModal, setActionModal] = useState({ type: null, loan: null });
  const [editFormData, setEditFormData] = useState({});
  const [payAmount, setPayAmount] = useState("");
  const [closeRemarks, setCloseRemarks] = useState("");

  const handleAction = (type, loan) => {
    setActionModal({ type, loan });
    if (type === "edit") {
      setEditFormData({
        amount: loan.amount,
        interest: loan.interest,
        startDate: loan.startDate,
        dueDate: loan.dueDate,
        outstanding: loan.outstanding,
      });
    } else if (type === "pay") {
      setPayAmount("");
      setCloseRemarks("");
    } else if (type === "close") {
      setCloseRemarks("");
    }
  };

  const submitEdit = async () => {
    const loan = actionModal.loan;
    try {
      dispatch(setLoading(true));
      const newFormBlock = {
        ...loan.originalData.form,
        loanAmount: editFormData.amount,
        interest: editFormData.interest,
        startDate: editFormData.startDate,
        endDate: editFormData.dueDate,
      };
      await updateDoc(doc(db, "billing", loan.dbId), {
        form: newFormBlock,
        outstanding: Number(editFormData.outstanding),
      });
      toast.dismiss();
      toast.success("Updated successfully");
      setActionModal({ type: null, loan: null });
      fetchLoans();
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to update");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  const submitPay = async () => {
    const loan = actionModal.loan;
    const payment = Number(payAmount) || 0;
    if (payment <= 0) return toast.error("Enter valid amount");
    if (payment > loan.outstanding) return toast.error("Payment exceeds outstanding");

    try {
      dispatch(setLoading(true));
      const newOutstanding = loan.outstanding - payment;
      const paymentRecord = {
        amount: payment,
        date: new Date().toISOString()
      };
      if (newOutstanding === 0) {
        paymentRecord.isClosing = true;
        paymentRecord.remarks = closeRemarks;
      }

      await updateDoc(doc(db, "billing", loan.dbId), {
        outstanding: newOutstanding,
        payments: arrayUnion(paymentRecord)
      });
      toast.dismiss();
      toast.success("Payment recorded");
      setActionModal({ type: null, loan: null });
      fetchLoans();
    } catch (err) {
      dispatch(setLoading(false));
      toast.error("Failed to process");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  const submitClose = async () => {
    const loan = actionModal.loan;
    try {
      dispatch(setLoading(true));
      await updateDoc(doc(db, "billing", loan.dbId), {
        outstanding: 0,
        payments: arrayUnion({
          amount: loan.outstanding,
          date: new Date().toISOString(),
          isClosing: true,
          remarks: closeRemarks
        })
      });
      toast.dismiss();
      toast.success("Loan closed");
      setActionModal({ type: null, loan: null });
      fetchLoans();
    } catch (err) {
      dispatch(setLoading(false));
      toast.dismiss();
      toast.error("Failed to close loan");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  /* ===== FETCH BILLING DATA ===== */
  const fetchLoans = async () => {
    dispatch(setLoading(true));
    try {
      const q = query(collection(db, "billing"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      const fetchedLoans = snap.docs.map((d) => {
        const data = d.data();
        const form = data.form || {};
        const ornaments = data.ornaments || [];

        // Sum up the net weight of all ornaments
        const totalWeight = ornaments.reduce((sum, o) => sum + Number(o.net || 0), 0);

        const amount = Number(form.loanAmount) || 0;
        const interest = Number(form.interest) || 0;
        const totalWithInterest = amount + (amount * interest / 100);

        const outstanding = data.outstanding !== undefined ? data.outstanding : totalWithInterest;
        const amountPaid = totalWithInterest - outstanding;

        const payments = data.payments || [];
        const closingPayment = payments.find(p => p.isClosing && p.remarks);

        return {
          dbId: d.id,
          originalData: data,
          loanId: data.customerId || d.id,
          name: form.customerName || "-",
          phone: form.phone || "-",
          weight: totalWeight,
          amount: amount,
          interest: interest,
          totalWithInterest: totalWithInterest,
          amountPaid: amountPaid > 0 ? amountPaid : 0,
          startDate: form.startDate || "-",
          dueDate: form.endDate || "-",
          outstanding: outstanding,
          remarks: closingPayment ? closingPayment.remarks : "-",
        };
      });

      setLoans(fetchedLoans);
    } catch (err) {
      console.error("Failed to fetch loans:", err);
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  /* ===== STATUS LOGIC ===== */
  const getStatus = (loan) => {
    const today = new Date();
    const due = new Date(loan.dueDate);

    if (loan.outstanding === 0) return "closed";
    if (today > due) return "overdue";
    return "active";
  };

  /* ===== SUMMARY ===== */
  const totalLoans = loans.length;
  const activeLoans = loans.filter(l => getStatus(l) === "active").length;
  const closedLoans = loans.filter(l => getStatus(l) === "closed").length;
  const overdueLoans = loans.filter(l => getStatus(l) === "overdue").length;

  const totalDisbursed = loans.reduce((sum, l) => sum + l.amount, 0);
  const totalOutstanding = loans.reduce((sum, l) => sum + l.outstanding, 0);

  /* ================= UI ================= */

  return (
    <Wrapper>

      {/* ===== CARDS ===== */}
      <Cards>
        <Card><Title>Total Loans</Title><Value>{totalLoans}</Value></Card>
        <Card><Title>Active Loans</Title><Value>{activeLoans}</Value></Card>
        <Card><Title>Closed Loans</Title><Value>{closedLoans}</Value></Card>
        <Card><Title>Overdue Loans</Title><Value>{overdueLoans}</Value></Card>
        <Card><Title>Total Disbursed</Title><Value>₹ {totalDisbursed}</Value></Card>
        <Card><Title>Total Outstanding</Title><Value>₹ {totalOutstanding}</Value></Card>
      </Cards>

      {/* ===== DESKTOP TABLE ===== */}
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Loan ID</Th>
              <Th>Customer</Th>
              <Th>Phone</Th>
              <Th>Gold</Th>
              <Th>Loan</Th>
              <Th>Interest</Th>
              <Th>Start</Th>
              <Th>Due</Th>
              <Th>Outstanding</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>

          <tbody>
            {loans.map((loan, i) => {
              const status = getStatus(loan);
              console.log("loan", loan);
              return (
                <tr key={i}>
                  <Td>{loan.loanId}</Td>
                  <Td>{loan.name}</Td>
                  <Td>{loan.phone}</Td>
                  <Td>{loan.weight} g</Td>
                  <Td>₹ {loan.amount}</Td>
                  <Td>{loan.interest}%</Td>
                  <Td>{loan.startDate}</Td>
                  <Td>{loan.dueDate}</Td>
                  <Td>₹ {loan.outstanding}</Td>

                  <Td>
                    <Status type={status}>{status}</Status>
                  </Td>

                  <Td>
                    <Actions>
                      <Btn type="view" onClick={() => handleAction("view", loan)} title="View"><FiEye /></Btn>
                      <Btn type="edit" onClick={() => handleAction("edit", loan)} title="Edit"><FiEdit /></Btn>
                      <Btn type="pay" onClick={() => handleAction("pay", loan)} title="Pay"><FiDollarSign /></Btn>
                      <Btn type="close" onClick={() => handleAction("close", loan)} title="Close"><FiLock /></Btn>
                    </Actions>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      {/* ===== MOBILE CARDS ===== */}
      <MobileContainer>
        {loans.map((loan, i) => {
          const status = getStatus(loan);

          return (
            <MobileCard key={i}>
              <Row><Label>Loan ID</Label><Val>{loan.loanId}</Val></Row>
              <Row><Label>Name</Label><Val>{loan.name}</Val></Row>
              <Row><Label>Phone</Label><Val>{loan.phone}</Val></Row>
              <Row><Label>Loan</Label><Val>₹ {loan.amount}</Val></Row>
              <Row><Label>Outstanding</Label><Val>₹ {loan.outstanding}</Val></Row>

              <Row>
                <Label>Status</Label>
                <Status type={status}>{status}</Status>
              </Row>

              <Actions>
                <Btn type="view" onClick={() => handleAction("view", loan)}><FiEye /></Btn>
                <Btn type="edit" onClick={() => handleAction("edit", loan)}><FiEdit /></Btn>
                <Btn type="pay" onClick={() => handleAction("pay", loan)}><FiDollarSign /></Btn>
                <Btn type="close" onClick={() => handleAction("close", loan)}><FiLock /></Btn>
              </Actions>
            </MobileCard>
          );
        })}
      </MobileContainer>

      {/* ===== ACTION MODALS ===== */}
      {
        actionModal.type === "view" && (
          <ModalOverlay onClick={() => setActionModal({ type: null, loan: null })}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: 10 }}>View Loan Details</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <FormGroup><FormLabel>Loan ID</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.loanId}</div></FormGroup>
                <FormGroup><FormLabel>Customer Name</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.name}</div></FormGroup>
                <FormGroup><FormLabel>Phone</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.phone}</div></FormGroup>
                <FormGroup><FormLabel>Loan Amount</FormLabel><div style={{ fontWeight: 600 }}>₹{actionModal.loan.amount}</div></FormGroup>
                <FormGroup><FormLabel>Total Amount with Interest</FormLabel><div style={{ fontWeight: 600 }}>₹{actionModal.loan.totalWithInterest}</div></FormGroup>
                <FormGroup><FormLabel>Outstanding</FormLabel><div style={{ fontWeight: 600, color: "#991b1b" }}>₹{actionModal.loan.outstanding}</div></FormGroup>
                <FormGroup><FormLabel>Amount Paid Till Date</FormLabel><div style={{ fontWeight: 600 }}>₹{actionModal.loan.amountPaid}</div></FormGroup>
                <FormGroup><FormLabel>Interest</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.interest}%</div></FormGroup>
                <FormGroup><FormLabel>Start Date</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.startDate}</div></FormGroup>
                <FormGroup><FormLabel>Due Date</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.dueDate}</div></FormGroup>
                <FormGroup><FormLabel>Total Gold (Net)</FormLabel><div style={{ fontWeight: 600 }}>{actionModal.loan.weight} g</div></FormGroup>
                <FormGroup><FormLabel>Status</FormLabel><div style={{ fontWeight: 600, textTransform: "capitalize" }}>{getStatus(actionModal.loan)}</div></FormGroup>
                {actionModal.loan.remarks !== "-" && (
                  <FormGroup style={{ gridColumn: "1 / -1" }}>
                    <FormLabel>Closing Remarks</FormLabel>
                    <div style={{ fontWeight: 600 }}>{actionModal.loan.remarks}</div>
                  </FormGroup>
                )}
              </div>
              <ModalActions>
                <ModalBtn onClick={() => setActionModal({ type: null, loan: null })}>Close</ModalBtn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )
      }

      {
        actionModal.type === "edit" && (
          <ModalOverlay onClick={() => setActionModal({ type: null, loan: null })}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: 10 }}>Edit Loan Details</h3>
              <FormGroup>
                <FormLabel>Loan Amount</FormLabel>
                <FormInput value={editFormData.amount || ""} onChange={(e) => {
                  const val = e.target.value.replace(/[^\d.]/g, "");
                  const amountNum = Number(val) || 0;
                  const interestNum = Number(editFormData.interest) || 0;
                  const totalWithInterest = amountNum + (amountNum * interestNum / 100);
                  const amountPaid = actionModal.loan?.amountPaid || 0;
                  const newOutstanding = totalWithInterest - amountPaid > 0 ? totalWithInterest - amountPaid : 0;
                  setEditFormData({ ...editFormData, amount: val, outstanding: newOutstanding });
                }} />
              </FormGroup>
              <FormGroup>
                <FormLabel>Interest (%)</FormLabel>
                <FormInput value={editFormData.interest || ""} onChange={(e) => {
                  const val = e.target.value.replace(/[^\d.]/g, "");
                  const amountNum = Number(editFormData.amount) || 0;
                  const interestNum = Number(val) || 0;
                  const totalWithInterest = amountNum + (amountNum * interestNum / 100);
                  const amountPaid = actionModal.loan?.amountPaid || 0;
                  const newOutstanding = totalWithInterest - amountPaid > 0 ? totalWithInterest - amountPaid : 0;
                  setEditFormData({ ...editFormData, interest: val, outstanding: newOutstanding });
                }} />
              </FormGroup>
              <FormGroup>
                <FormLabel>Outstanding</FormLabel>
                <FormInput value={editFormData.outstanding || ""} onChange={(e) => setEditFormData({ ...editFormData, outstanding: e.target.value.replace(/[^\d.]/g, "") })} />
              </FormGroup>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <FormGroup>
                  <FormLabel>Start Date</FormLabel>
                  <FormInput type="date" value={editFormData.startDate || ""} onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Due Date</FormLabel>
                  <FormInput type="date" value={editFormData.dueDate || ""} onChange={(e) => setEditFormData({ ...editFormData, dueDate: e.target.value })} />
                </FormGroup>
              </div>
              <ModalActions>
                <ModalBtn onClick={() => setActionModal({ type: null, loan: null })}>Cancel</ModalBtn>
                <ModalBtn primary onClick={submitEdit}>Save Changes</ModalBtn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )
      }

      {
        actionModal.type === "pay" && (
          <ModalOverlay onClick={() => setActionModal({ type: null, loan: null })}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: 10 }}>Process Payment</h3>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Current Outstanding</div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#991b1b" }}>₹{actionModal.loan.outstanding}</div>
              </div>
              <FormGroup>
                <FormLabel>Amount to Pay</FormLabel>
                <FormInput
                  autoFocus
                  placeholder="Enter amount"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value.replace(/\D/g, ""))}
                />
              </FormGroup>
              {Number(payAmount) === actionModal.loan.outstanding && (
                <FormGroup>
                  <FormLabel>Closing Remarks (Optional)</FormLabel>
                  <FormInput
                    placeholder="Enter remarks..."
                    value={closeRemarks}
                    onChange={(e) => setCloseRemarks(e.target.value)}
                  />
                </FormGroup>
              )}
              <ModalActions>
                <ModalBtn onClick={() => setActionModal({ type: null, loan: null })}>Cancel</ModalBtn>
                <ModalBtn primary onClick={submitPay}>Confirm Payment</ModalBtn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )
      }

      {
        actionModal.type === "close" && (
          <ModalOverlay onClick={() => setActionModal({ type: null, loan: null })}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: 10 }}>Close Loan</h3>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Outstanding to be waived/marked closed</div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#991b1b" }}>₹{actionModal.loan.outstanding}</div>
              </div>
              <FormGroup>
                <FormLabel>Closing Remarks</FormLabel>
                <FormInput
                  autoFocus
                  placeholder="Enter remarks..."
                  value={closeRemarks}
                  onChange={(e) => setCloseRemarks(e.target.value)}
                />
              </FormGroup>
              <ModalActions>
                <ModalBtn onClick={() => setActionModal({ type: null, loan: null })}>Cancel</ModalBtn>
                <ModalBtn primary onClick={submitClose}>Close Loan</ModalBtn>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )
      }
    </Wrapper >
  );
}